import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, AlertTriangle, BookOpen, ChevronRight, Copy, Check } from 'lucide-react';
import { LEARN_BY_SLUG } from '../data/learnArticles';

const SITE = 'https://githubmate.ai';

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border border-red-200',
  high:     'bg-orange-100 text-orange-800 border border-orange-200',
  medium:   'bg-yellow-100 text-yellow-700 border border-yellow-200',
};

const LANG_LABEL: Record<string, string> = {
  python: 'Python',
  typescript: 'TypeScript',
  javascript: 'JavaScript',
  yaml: 'YAML',
  solidity: 'Solidity',
};

function CodeBlock({ language, filename, code }: { language: string; filename: string; code: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-gray-300">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/60" />
            <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
            <span className="w-3 h-3 rounded-full bg-green-500/60" />
          </div>
          <span className="text-xs font-mono text-gray-400">{filename}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 uppercase tracking-wider">
            {LANG_LABEL[language] ?? language}
          </span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
            aria-label="Copy code"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-lime" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      {/* Code */}
      <pre className="bg-gray-900 text-gray-100 text-sm leading-relaxed p-5 overflow-x-auto font-mono whitespace-pre">
        {code}
      </pre>
    </div>
  );
}

export default function LearnArticle() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? LEARN_BY_SLUG[slug] : undefined;

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Article not found</h1>
        <p className="text-gray-600 mb-6">This vulnerability guide doesn't exist yet.</p>
        <Link to="/learn" className="btn-primary inline-flex items-center gap-2">
          <BookOpen className="h-4 w-4" /> View all guides
        </Link>
      </div>
    );
  }

  const related = article.relatedSlugs
    .map(s => LEARN_BY_SLUG[s])
    .filter(Boolean);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: article.title,
    description: article.summary,
    url: `${SITE}/learn/${article.slug}`,
    author: { '@type': 'Organization', name: 'GitHubMate' },
    keywords: article.tags.join(', '),
    about: { '@type': 'Thing', name: article.owasp },
  };

  return (
    <>
      <Helmet>
        <title>{article.title} | GitHubMate Learn</title>
        <meta name="description" content={article.subtitle} />
        <meta name="keywords" content={article.tags.join(', ')} />
        <link rel="canonical" href={`${SITE}/learn/${article.slug}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.subtitle} />
        <meta property="og:url" content={`${SITE}/learn/${article.slug}`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-charcoal-bg min-h-screen">
        <div className="max-w-4xl mx-auto px-4 pt-10 pb-16">

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-8 flex items-center gap-1 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/learn" className="hover:text-blue-600">Learn</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-gray-700 truncate max-w-xs">{article.title}</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${SEVERITY_BADGE[article.severity]}`}>
                {article.severity.toUpperCase()}
              </span>
              <span className="text-xs font-mono bg-blue-50 text-blue-800 border border-blue-200 px-2.5 py-1 rounded-full">
                {article.owasp.split(' - ')[0]}
              </span>
              <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
                {article.cwe}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
              {article.title}
            </h1>
            <p className="text-xl text-gray-600 mb-6">{article.subtitle}</p>
            <div className="flex flex-wrap gap-2">
              {article.tags.map(tag => (
                <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Summary */}
          <section className="card mb-8">
            <h2 className="font-bold text-gray-900 text-lg mb-3">What is this vulnerability?</h2>
            <p className="text-gray-700 leading-relaxed">{article.summary}</p>
          </section>

          {/* Real-world source */}
          <section className="mb-8">
            <div className="rounded-xl border-l-4 border-orange-400 bg-orange-50 px-6 py-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <h2 className="font-bold text-orange-900 text-sm uppercase tracking-wide">
                  Real-World Source
                </h2>
              </div>
              <p className="text-sm font-semibold text-orange-800 mb-2">{article.realWorld.source}</p>
              <p className="text-gray-700 text-sm leading-relaxed">{article.realWorld.explanation}</p>
            </div>
          </section>

          {/* Vulnerable code */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
              <h2 className="font-bold text-gray-900 text-lg">Vulnerable Code</h2>
            </div>
            <CodeBlock
              language={article.vulnerableCode.language}
              filename={article.vulnerableCode.filename}
              code={article.vulnerableCode.code}
            />
          </section>

          {/* Fixed code */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-lime flex-shrink-0" />
              <h2 className="font-bold text-gray-900 text-lg">Fixed Code</h2>
            </div>
            <CodeBlock
              language={article.fixedCode.language}
              filename={article.fixedCode.filename}
              code={article.fixedCode.code}
            />
          </section>

          {/* Detection */}
          <section className="mb-10">
            <div className="rounded-xl bg-gray-900 text-white px-6 py-5">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-4 w-4 text-lime flex-shrink-0" />
                <h2 className="font-bold text-lime text-sm uppercase tracking-wide">
                  How GitHubMate Detects This
                </h2>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">{article.detection}</p>
              <code className="text-xs text-lime bg-gray-800 px-2 py-1 rounded font-mono">
                pattern: {article.patternId}
              </code>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-xl border border-lime/30 bg-lime/5 px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 mb-10">
            <div>
              <p className="font-bold text-gray-900">Check your repo for {article.cwe}</p>
              <p className="text-sm text-gray-600">Free scan — no signup, runs in your browser in 30 seconds.</p>
            </div>
            <Link to="/" className="btn-primary whitespace-nowrap inline-flex items-center gap-2 flex-shrink-0">
              <Shield className="h-4 w-4" /> Run a free scan
            </Link>
          </div>

          {/* Related articles */}
          {related.length > 0 && (
            <section>
              <h2 className="font-bold text-gray-900 text-lg mb-4">Related Vulnerabilities</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {related.map(rel => (
                  <Link
                    key={rel.slug}
                    to={`/learn/${rel.slug}`}
                    className="card hover:shadow-md hover:border-lime/40 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SEVERITY_BADGE[rel.severity]}`}>
                        {rel.severity.toUpperCase()}
                      </span>
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-lime transition-colors" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug group-hover:text-lime transition-colors">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{rel.subtitle}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
